import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { useIsAwaitingOneDrive } from 'components/dms/useIsAwaitingOneDrive';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import {
    ActiveChip,
    META_LINK_CLASS,
    MetaItem,
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useParams } from 'react-router-dom';
import { useGetTeamByIdQuery } from 'store/api/teams';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useDmsTeamFieldGate } from './form-sections/useTeamForm';
import EmployeesView from './views/EmployeesView';
import GeneralView from './views/GeneralView';

const USED_CLASSIFIERS = ['TEAM_TYPE', 'BUSINESS_AREA_TYPE'];
const UPDATE_TEAMS = ['UPDATE.TEAMS'];

const TeamPage = (): JSX.Element => {
    const { id: teamId } = useParams();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/teams');
    const isNew = useIsNewDesign();
    const canEdit = useHasPermissions(UPDATE_TEAMS);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: team, isLoading: isLoadingTeam } = useGetTeamByIdQuery(teamId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!team?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/teams');
    const draftActions = useDraftActions({
        draft: team?.isDraft ? { type: 'team', id: team.id } : null,
        createRoute: '/team/new',
        onDeleted: leaveDeleted,
    });
    const {
        classifiers,
        allClassifiers,
        isLoading: isLoadingClassifiers,
    } = useClassifiers({ codes: USED_CLASSIFIERS });
    // The new design's Team card places its DMS group row by the OneDrive answer, so the page asks for it with the
    // team: a card drawn before the answer would move its rows when it lands.
    const { hasDmsTeamField } = useDmsTeamFieldGate();
    const isAwaitingOneDrive = useIsAwaitingOneDrive({ skip: !isNew || !hasDmsTeamField });

    const tabs = useMemo<TabType[]>(() => {
        if (!team) return [];
        return [
            {
                id: 'general',
                label: t('matters.general'),
                component: (
                    <GeneralView team={team} classifiers={classifiers} allClassifiers={allClassifiers} isNew={isNew} />
                ),
            },
            {
                id: 'employees',
                label: t('employees.employees'),
                // Nobody is a member of a draft team.
                hidden: isDraft,
                component: <EmployeesView teamEmployees={team.teamEmployees ?? []} />,
            },
        ];
    }, [team, classifiers, allClassifiers, isNew, isDraft]);

    if (isLoadingTeam || isLoadingClassifiers || isAwaitingOneDrive) {
        return <Loader />;
    }

    if (!team) {
        return <></>;
    }

    const tabsGallery = (
        <TabsGallery
            tabs={tabs}
            tabGroupId="team-page"
            trackTabsInHistoryState
            usePathBasedRouting
            wrapperClassName={RECORD_TABS_CLASS}
            panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
            className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
        />
    );

    const title = team.name || (isDraft ? t('new-entity.team') : '-');
    const draft = draftActions.draft;
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;

    if (isNew) {
        const typeLabel = team.teamTypeCode ? getClassifierText(classifiers['TEAM_TYPE'], team.teamTypeCode) : '';
        const offices = (team.teamOffices ?? []).map((office) => office.name).join(', ');
        const header = (
            <RecordHeader
                number={team.abbreviation}
                chips={
                    <>
                        {isDraft ? <DraftChip /> : null}
                        {typeLabel ? <StatusChip type="neutral" label={typeLabel} /> : null}
                        <ActiveChip isActive={!!team.isActive} />
                    </>
                }
                title={title}
                pin={isDraft ? undefined : { kind: 'team', id: team.id }}
                meta={
                    <>
                        {team.parentTeam?.id ? (
                            <MetaItem label={<FormattedMessage id="teams.parent-team" />}>
                                <RecordLink
                                    className={META_LINK_CLASS}
                                    to={`/team/${team.parentTeam.id}`}
                                    label={team.parentTeam.name}
                                />
                            </MetaItem>
                        ) : null}
                        {offices ? (
                            <MetaItem label={<FormattedMessage id="teams.offices" />}>{offices}</MetaItem>
                        ) : null}
                        {team.emailAddress ? (
                            <MetaItem label={<FormattedMessage id="email" />}>
                                <a className={META_LINK_CLASS} href={`mailto:${team.emailAddress}`}>
                                    {team.emailAddress}
                                </a>
                            </MetaItem>
                        ) : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </>
                }
                actions={isDraft ? <DraftRecordActions actions={draftActions} /> : undefined}
            />
        );
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage
                    header={
                        draftNotice ? (
                            <div className="flex flex-col gap-4">
                                {header}
                                {draftNotice}
                            </div>
                        ) : (
                            header
                        )
                    }
                >
                    <SectionEditProvider canEdit={canEdit && !isDraft}>{tabsGallery}</SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    return (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout className="flex h-full flex-col overflow-hidden">
                <PageHeader
                    title={title}
                    showBreadcrumb
                    onBack={goBack}
                    buttons={
                        <div className="flex items-center justify-end gap-2">
                            {isDraft ? (
                                <DraftRecordActions actions={draftActions} />
                            ) : (
                                <PermissionButton
                                    permissions={UPDATE_TEAMS}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigateWithReturn(`/team/${teamId}/edit`)}
                                >
                                    <FormattedMessage id="edit" />
                                </PermissionButton>
                            )}
                        </div>
                    }
                >
                    {draft ? (
                        <div className="flex flex-wrap items-center gap-2 py-3">
                            <DraftChip />
                            <DraftMeta draft={draft} />
                        </div>
                    ) : null}
                </PageHeader>
                {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
                {tabsGallery}
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );
};

export default TeamPage;
